import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, PromptSelector, toast, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Calculator } from 'lucide-react';
import { useState } from 'react';

interface RecalculateDetailsActionProps {
  tourId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog action that recalculates tour details (duration, stops, difficulty,
 * walking estimate, highlights, tagline) based on current attractions using AI.
 * @param props - Component props including tourId, open state, and onOpenChange callback.
 */
export function RecalculateDetailsAction({ tourId, open, onOpenChange }: RecalculateDetailsActionProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [promptId, setPromptId] = useState<string | undefined>();

  const recalculateMutation = useMutation({
    mutationFn: async (data: { promptId?: string }) => {
      return apiClient.post(`/tours/${tourId}/generate-details`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Tour', tourId] });
      onOpenChange(false);
      toast.success('Tour details recalculated successfully');
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to recalculate tour details');
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Recalculate Tour Details</DialogTitle>
          <DialogDescription>
            This will recalculate tour details (duration, difficulty, walking estimate,
            highlights, and tagline) based on the current attractions using AI.
            Existing values will be overwritten.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <PromptSelector
            purpose="tour_content"
            value={promptId}
            onChange={setPromptId}
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={() => recalculateMutation.mutate({ promptId })}
            loading={recalculateMutation.isPending}
          >
            Recalculate Details
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Icon used for the Recalculate Details action button. */
export const RecalculateDetailsIcon = Calculator;
